<template>
  <div class="mt-card p-5 mb-6">
    <!-- Prompt -->
    <div class="mb-4">

      <ContentRow
        v-for="row in sorted(container.questionContents)"
        :key="'q-' + row.rownum"
        :row="row"
      />
    </div>

    <!-- Answers -->
    <div class="mb-4">
      <!-- OPTION (multiple choice) -->
      <div v-if="isOption" class="space-y-3">
        <label
          v-for="opt in sorted(container.options)"
          :key="opt.optionValue"
          class="flex items-center gap-3 cursor-pointer p-3 rounded-lg border-2 transition-all"
          :class="selectedOption === opt.optionValue
            ? 'option-selected'
            : 'option-default'"
        >
          <input
            type="radio"
            :name="radioName"
            :value="opt.optionValue"
            v-model="selectedOption"
            :disabled="submitted"
            class="w-4 h-4 shrink-0 accent-[var(--mt-accent-blue)]"
          />
          <div class="flex-1">
            <!-- content only (no A/B/C labels) -->
            <ContentRow :row="opt" />
          </div>
        </label>
      </div>

      <!-- BOOLEAN (Yes/No radio buttons) -->
      <div v-else-if="isBoolean" class="flex gap-4">
        <label
          class="flex items-center gap-2 cursor-pointer px-4 py-2 rounded-lg border-2 transition-all"
          :class="booleanAnswer === true
            ? 'option-selected'
            : 'option-default'"
        >
          <input
            type="radio"
            :name="radioName"
            :value="true"
            v-model="booleanAnswer"
            :disabled="submitted"
            class="sr-only"
          />
          <MTIcon :name="booleanAnswer === true ? 'check-circle' : 'circle'" :size="20" :style="booleanAnswer === true ? { color: 'var(--mt-accent-blue)' } : {}" :class="booleanAnswer !== true ? 'text-[var(--mt-text-muted)]' : ''" />
          <span class="font-semibold">Yes</span>
        </label>
        <label
          class="flex items-center gap-2 cursor-pointer px-4 py-2 rounded-lg border-2 transition-all"
          :class="booleanAnswer === false
            ? 'option-selected'
            : 'option-default'"
        >
          <input
            type="radio"
            :name="radioName"
            :value="false"
            v-model="booleanAnswer"
            :disabled="submitted"
            class="sr-only"
          />
          <MTIcon :name="booleanAnswer === false ? 'check-circle' : 'circle'" :size="20" :style="booleanAnswer === false ? { color: 'var(--mt-accent-blue)' } : {}" :class="booleanAnswer !== false ? 'text-[var(--mt-text-muted)]' : ''" />
          <span class="font-semibold">No</span>
        </label>
      </div>

      <!-- COMMA_SEPERATED_BIGDECIMAL / COMMA_SEPERATED_BIGDECIMAL_ORDERED -->
      <div v-else-if="isCommaSeparated || isCommaSeparatedOrdered">
        <input
          ref="answerInput"
          v-model="typedAnswer"
          type="text"
          :disabled="submitted"
          class="mt-input w-full"
          :placeholder="isCommaSeparatedOrdered ? 'Enter numbers in order (e.g., 1, 2, 3)' : 'Enter numbers separated by commas (e.g., 1, 2, 3)'"
          inputmode="text"
          @keydown.enter="submitIfReady"
        />
        <p class="text-xs text-[var(--mt-text-muted)] mt-1">
          {{ isCommaSeparatedOrdered ? 'Order matters' : 'Order does not matter' }}
        </p>
      </div>

      <!-- BIGDECIMAL / STRING free entry -->
      <div v-else>
        <input
          ref="answerInput"
          v-model="typedAnswer"
          type="text"
          :disabled="submitted"
          class="mt-input w-full"
          placeholder="Type your answer"
          :inputmode="isNumeric ? 'decimal' : 'text'"
          @keydown.enter="submitIfReady"
        />
      </div>
    </div>

    <!-- Submit + result -->
    <div class="flex items-center gap-3">
      <button
        v-if="!submitted"
        class="mt-btn mt-btn-primary"
        :disabled="!canSubmit"
        @click="submit"
      >
        Submit
      </button>

      <!-- Review mode: show "Answer locked" after submit (no correct/wrong feedback) -->
      <span v-if="submitted && reviewMode && !showReview" class="text-sm font-semibold inline-flex items-center gap-1" :style="{ color: 'var(--mt-accent-blue)' }">
        <MTIcon name="lock" :size="16" />
        Answer locked
      </span>

      <!-- Normal mode OR showReview: show correct/wrong feedback -->
      <span v-if="submitted && (!reviewMode || showReview)" class="text-sm font-semibold inline-flex items-center gap-1" :class="correct ? 'text-green-600' : 'text-red-600'">
        <MTIcon :name="correct ? 'check-circle' : 'x-circle'" :size="16" />
        {{ correct ? "Correct" : "Not quite" }}
      </span>
    </div>

    <!-- Correct answer: shown when wrong in normal mode, or in review mode when showReview=true -->
    <div v-if="submitted && (!reviewMode || showReview) && !correct" class="flex items-start gap-2 mt-3">
      <MTIcon name="check-circle" :size="18" class="text-green-600 mt-0.5 shrink-0" />
      <div>
        <span class="text-sm font-medium text-[var(--mt-text-muted)]">Correct answer: </span>
        <span class="text-sm text-green-500 font-medium">
          {{ formatCorrectAnswer() }}
        </span>
      </div>
    </div>

    <!-- Expandable solution: show in normal mode, or in review mode only when showReview=true -->
    <div v-if="submitted && (!reviewMode || showReview) && (container.solutionContents?.length || 0) > 0" class="mt-3">
      <button
        class="text-xs font-medium flex items-center gap-1"
        :style="{ color: 'var(--mt-accent-blue)' }"
        @click="solutionExpanded = !solutionExpanded"
      >
        <MTIcon :name="solutionExpanded ? 'chevron-down' : 'chevron-right'" :size="14" />
        {{ solutionExpanded ? 'Hide solution' : 'Show solution' }}
      </button>
      <div v-if="solutionExpanded" class="mt-2 pl-4 border-l-2 border-[var(--mt-border)]">
        <ContentRow
          v-for="row in sorted(container.solutionContents)"
          :key="'s-' + row.rownum"
          :row="row"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import ContentRow from "./ContentRow.vue";
import MTIcon from "@/components/ui/MTIcon.vue";

const props = defineProps({
  container: { type: Object, required: true },
  index: { type: Number, required: true },
  reviewMode: { type: Boolean, default: false },
  showReview: { type: Boolean, default: false }
});

const emit = defineEmits(["containerAnswered"]);

const submitted = ref(false);
const correct = ref(false);
const solutionExpanded = ref(false);

const selectedOption = ref(null);
const typedAnswer = ref("");
const answerInput = ref(null);

// Auto-focus the text input on mount (so user can type immediately after Next/Skip)
onMounted(() => {
  nextTick(() => {
    answerInput.value?.focus();
  });
});

function submitIfReady() {
  if (canSubmit.value) submit();
}
const booleanAnswer = ref(null);

// Answer type detection
const isOption = computed(() => props.container.answerDatatype === "OPTION");
const isNumeric = computed(() => props.container.answerDatatype === "BIGDECIMAL");
const isBoolean = computed(() => props.container.answerDatatype === "BOOLEAN");
const isCommaSeparated = computed(() => props.container.answerDatatype === "COMMA_SEPERATED_BIGDECIMAL");
const isCommaSeparatedOrdered = computed(() => props.container.answerDatatype === "COMMA_SEPERATED_BIGDECIMAL_ORDERED");

const radioName = computed(() => `container-${props.index}`);

const canSubmit = computed(() => {
  if (submitted.value) return false;
  if (isOption.value) return selectedOption.value !== null;
  if (isBoolean.value) return booleanAnswer.value !== null;
  return typedAnswer.value.trim().length > 0;
});

function sorted(arr) {
  return (arr || []).slice().sort((a, b) => Number(a.rownum) - Number(b.rownum));
}

/**
 * Correct answer rules (from your guidance + file):
 * - OPTION: correct is answerContents[0].rowContent (e.g. "C")
 * - BIGDECIMAL: numeric comparison with small tolerance
 * - BOOLEAN: TRUE/FALSE stored, accept yes/no, true/false, y/n
 * - COMMA_SEPERATED_BIGDECIMAL: unordered comma-separated numbers
 * - COMMA_SEPERATED_BIGDECIMAL_ORDERED: ordered comma-separated numbers
 */
function getCorrectAnswer() {
  const a0 = (props.container.answerContents || [])[0];
  if (!a0) return null;
  return (a0.rowContent ?? "").toString().trim();
}

function formatCorrectAnswer() {
  const answer = getCorrectAnswer();
  if (answer === null || answer === undefined) return "—";

  if (isBoolean.value) {
    return answer.toUpperCase() === "TRUE" ? "Yes" : "No";
  }

  if (isOption.value && props.container.options?.length) {
    const opt = props.container.options.find(o => o.optionValue === answer);
    if (opt) return opt.rowContent || answer;
  }

  return String(answer);
}

// Small tolerance for floating-point comparisons (0.0001)
const NUMERIC_TOLERANCE = 0.0001;

function stripNonNumeric(str) {
  // Strip everything except digits, decimal point, and minus sign
  // Handles thousand separators ("1,000"), currency ("£5"), units ("30p"), etc.
  return String(str).replace(/[^0-9.\-]/g, '');
}

function numbersEqual(a, b) {
  const numA = Number(stripNonNumeric(a));
  const numB = Number(stripNonNumeric(b));
  if (!Number.isFinite(numA) || !Number.isFinite(numB)) return false;
  return Math.abs(numA - numB) < NUMERIC_TOLERANCE;
}

function parseCommaSeparated(str) {
  // Support both "1,2,3" and "1, 2, 3" formats
  return str.split(",").map(s => s.trim()).filter(s => s.length > 0);
}

function checkCommaSeparatedUnordered(studentStr, expectedStr) {
  const studentVals = parseCommaSeparated(studentStr);
  const expectedVals = parseCommaSeparated(expectedStr);

  if (studentVals.length !== expectedVals.length) return false;

  // Check each student value matches one expected value (unordered)
  const usedIndices = new Set();
  for (const sVal of studentVals) {
    let found = false;
    for (let i = 0; i < expectedVals.length; i++) {
      if (!usedIndices.has(i) && numbersEqual(sVal, expectedVals[i])) {
        usedIndices.add(i);
        found = true;
        break;
      }
    }
    if (!found) return false;
  }
  return true;
}

function checkCommaSeparatedOrdered(studentStr, expectedStr) {
  const studentVals = parseCommaSeparated(studentStr);
  const expectedVals = parseCommaSeparated(expectedStr);

  if (studentVals.length !== expectedVals.length) return false;

  // Check each value in order
  for (let i = 0; i < studentVals.length; i++) {
    if (!numbersEqual(studentVals[i], expectedVals[i])) return false;
  }
  return true;
}

function checkBoolean(studentVal, expectedStr) {
  // Expected is stored as "TRUE" or "FALSE"
  const expectedBool = expectedStr.toUpperCase() === "TRUE";
  return studentVal === expectedBool;
}

function submit() {
  const expected = getCorrectAnswer();

  let studentAnswer = null;
  let isCorrect = false;

  if (isOption.value) {
    studentAnswer = selectedOption.value;
    isCorrect = expected != null && studentAnswer === expected;
  } else if (isBoolean.value) {
    studentAnswer = booleanAnswer.value;
    isCorrect = expected != null && checkBoolean(studentAnswer, expected);
  } else if (isCommaSeparated.value) {
    studentAnswer = typedAnswer.value.trim();
    isCorrect = expected != null && checkCommaSeparatedUnordered(studentAnswer, expected);
  } else if (isCommaSeparatedOrdered.value) {
    studentAnswer = typedAnswer.value.trim();
    isCorrect = expected != null && checkCommaSeparatedOrdered(studentAnswer, expected);
  } else if (isNumeric.value) {
    studentAnswer = typedAnswer.value.trim();
    isCorrect = expected != null && numbersEqual(studentAnswer, expected);
  } else {
    // STRING or unknown type - case-insensitive match
    studentAnswer = typedAnswer.value.trim();
    isCorrect = expected != null && studentAnswer.toLowerCase() === expected.toLowerCase();
  }

  submitted.value = true;
  correct.value = isCorrect;

  emit("containerAnswered", {
    rownum: props.container.rownum,
    answerDatatype: props.container.answerDatatype,
    studentAnswer,
    correctAnswer: expected,
    correct: isCorrect,

    // for reporting
    questionContents: props.container.questionContents || [],
    options: props.container.options || [],
    solutionContents: props.container.solutionContents || []
  });
}
</script>

<style scoped>
.option-selected {
  border-color: var(--mt-accent-blue);
  background: var(--mt-accent-blue-muted);
  color: var(--mt-accent-blue);
}

.option-default {
  border-color: var(--mt-border);
}

.option-default:hover {
  border-color: var(--mt-text-muted);
  background: var(--mt-hover);
}
</style>
